/**
 * « Voir tous mes crédits » v2 (route account/services ; SPEC VC4, MC3 ; retour M4) — maquette
 * 18-compte-credits. Données = Services/ServicesHistory.tsx (GET client/services) ; actifs triés par
 * expiration la plus proche d'abord, expirés par expiration la plus récente d'abord.
 * Repris de la v1 : message de retour Alma (?alma_success=1, 5 s, puis l'URL est nettoyée) et page
 * « crédits en cours de traitement » quand aucun forfait n'est encore visible.
 */
import { useListClientServices } from 'hooks/useApiQueries';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import type { ClientService } from 'types/client';
import { AccountShell, BackLink, Notice, SectionHead, Tag } from './components/Ui';
import { Icon } from './components/Icon';
import { remainingTotal, splitServices, walletStatus, type WalletStatus } from './lib/credits';
import { fmtDateNum, plural, toDate } from './lib/format';

const STATUS: Record<WalletStatus, { label: string; tone: 'green' | 'amber' | 'neutral' }> = {
  active: { label: 'Actif', tone: 'green' },
  soon: { label: 'Expire bientôt', tone: 'amber' },
  empty: { label: 'Épuisé', tone: 'neutral' },
  expired: { label: 'Expiré', tone: 'neutral' },
};

const dateCell = (iso: string | null | undefined) => {
  const d = toDate(iso);
  return d ? fmtDateNum(d) : '—';
};

export function MyCredits() {
  const { data, isLoading } = useListClientServices();
  const services = useMemo(() => (data ? Object.values(data) : []), [data]);
  const { active, expired } = useMemo(() => splitServices(services), [services]);
  const location = useLocation();

  // = ServicesHistory.tsx : retour d'un paiement Alma réussi.
  const almaSuccess = new URLSearchParams(location.search).get('alma_success') === '1';
  const [showAlmaSuccess, setShowAlmaSuccess] = useState(almaSuccess);
  useEffect(() => {
    if (almaSuccess && services.length > 0) {
      const timer = setTimeout(() => {
        setShowAlmaSuccess(false);
        const params = new URLSearchParams(location.search);
        params.delete('alma_success');
        const search = params.toString();
        window.history.replaceState({}, '', search ? `${location.pathname}?${search}` : location.pathname);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [almaSuccess, services.length, location.search, location.pathname]);

  // Forfaits épuisés (encore valides, 0 séance) : avec les expirés, pas dans « Crédits actifs », dont le chapô ne
  // compte que les forfaits utilisables (revue du 04/10 : « 3 forfaits actifs » au-dessus de 4 lignes).
  const usable = active.filter((s) => s.Remaining > 0);
  const spent = [...active.filter((s) => s.Remaining <= 0), ...expired];

  return (
    <AccountShell className="mmv2-acct--page">
      <BackLink />
      <div className="mmv2-acct-pagehead">
        <h1 className="mmv2-acct-title mmv2-acct-title--ink">Mes crédits</h1>
        <Link className="mmv2-btn mmv2-btn--cta" to="/tarifs">
          Acheter des crédits
          <Icon name="right" />
        </Link>
      </div>
      {!isLoading && usable.length ? (
        <p className="mmv2-lede mmv2-acct-pagelede">
          {plural(remainingTotal(usable), 'séance restante', 'séances restantes')} sur{' '}
          {plural(usable.length, 'forfait actif', 'forfaits actifs')}.
        </p>
      ) : null}

      {showAlmaSuccess ? (
        <div className="mmv2-acct-pagenotice">
          <Notice tone="success" role="status">
            <b>Paiement Alma confirmé&nbsp;!</b> Vos crédits ont été ajoutés à votre compte. S’ils n’apparaissent pas encore,
            ils peuvent prendre quelques minutes à s’afficher.{' '}
            <button type="button" className="mmv2-linkish" onClick={() => window.location.reload()}>
              Actualiser
            </button>
          </Notice>
        </div>
      ) : null}

      {isLoading ? (
        <div className="mmv2-acct-skeleton mmv2-acct-skeleton--table" aria-busy="true" aria-label="Chargement de vos crédits" />
      ) : services.length === 0 ? (
        // = ServicesHistory.tsx : aucun forfait (souvent juste après un achat).
        <div className="mmv2-card mmv2-card--pad mmv2-acct-processing">
          <h2 className="mmv2-acct-processing__title">Vos crédits sont en cours de traitement</h2>
          <p>Votre achat a été confirmé avec succès !</p>
          <p className="mmv2-muted">
            Vos crédits peuvent prendre <b>quelques minutes</b> à apparaître dans votre compte. N’hésitez pas à actualiser
            cette page dans quelques instants.
          </p>
          <div className="mmv2-acct-actions mmv2-acct-actions--center">
            <button type="button" className="mmv2-btn mmv2-btn--ghost" onClick={() => window.location.reload()}>
              <Icon name="refresh" size={15} />
              Actualiser
            </button>
            <Link className="mmv2-btn mmv2-btn--quiet" to="/">
              Réserver un cours
            </Link>
          </div>
          <p className="mmv2-tiny mmv2-muted">Si vos crédits n’apparaissent pas après 10 minutes, contactez notre support.</p>
        </div>
      ) : (
        <>
          <section className="mmv2-acct-pagesection" aria-labelledby="mmv2-acct-active">
            <SectionHead id="mmv2-acct-active" title="Crédits actifs" />
            {usable.length ? (
              <CreditsTable services={usable} />
            ) : (
              <div className="mmv2-card mmv2-card--pad">
                <p className="mmv2-muted">
                  Aucun crédit actif.{' '}
                  <Link className="mmv2-linkish" to="/tarifs">
                    Voir les tarifs
                  </Link>
                </p>
              </div>
            )}
          </section>

          {spent.length ? (
            <section className="mmv2-acct-pagesection" aria-labelledby="mmv2-acct-expired">
              <SectionHead
                id="mmv2-acct-expired"
                title={spent.length > expired.length ? 'Crédits expirés ou épuisés' : 'Crédits expirés'}
              />
              <CreditsTable
                services={spent}
                expired
                dateLabel={spent.length > expired.length ? 'Expiration' : 'Expiré le'}
              />
            </section>
          ) : null}
        </>
      )}
    </AccountShell>
  );
}

function CreditsTable({
  services,
  expired = false,
  dateLabel = 'Expire le',
}: {
  services: ClientService[];
  expired?: boolean;
  /** En-tête de la colonne d'expiration. */
  dateLabel?: string;
}) {
  return (
    <div className="mmv2-card mmv2-acct-tablecard">
      <table className="mmv2-acct-dtable mmv2-acct-dtable--credits">
        <thead>
          <tr>
            <th scope="col">Forfait</th>
            <th scope="col" className="mmv2-acct-right">
              Séances restantes
            </th>
            <th scope="col">Activé le</th>
            <th scope="col">{dateLabel}</th>
            <th scope="col" className="mmv2-acct-right">
              Statut
            </th>
          </tr>
        </thead>
        <tbody>
          {services.map((service) => {
            const status = STATUS[walletStatus(service)];
            return (
              <tr key={service.Id} className={expired ? 'mmv2-acct-dim' : undefined}>
                <td data-label="Forfait" className="mmv2-acct-cell-name">
                  <b>{service.Name}</b>
                  {service.Program?.Name ? <span>{service.Program.Name}</span> : null}
                </td>
                <td data-label="Séances restantes" className="mmv2-acct-right mmv2-acct-cell-n">
                  <b>{service.Remaining}</b>
                  {service.Count ? <span>sur {service.Count}</span> : null}
                </td>
                <td data-label="Activé le">{dateCell(service.ActiveDate)}</td>
                <td data-label={dateLabel}>{dateCell(service.ExpirationDate)}</td>
                <td data-label="Statut" className="mmv2-acct-right mmv2-acct-cell-status">
                  <Tag tone={status.tone}>{status.label}</Tag>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
